import { CartModal } from "@@/carts";
import { useModal } from "@@/modals/use-modal";
import { useGiftCardPurchaseContext } from "@@/products/giftcards/gift-card-purchase.context";
import { useCalculatedProviderStatus } from "@@/providers/calculate-provider-status";
import { CartProviderCheckModal } from "@@/providers/cart-provider-check-modal";
import { ProviderPauseCheckModal } from "@@/providers/provider-pause-check-modal";
import { _ProductImages } from "@@/routes/products/$productId/_bookable/generic/_product_images";
import { browserLogger } from "@@/settings";
import { Conditional } from "@@/shared/conditional";
import { CurrentUserIcon } from "@@/shared/current-user-icon";
import {
    HorizontalDivider,
    HorizontalLine,
    VerticalDivider,
} from "@@/shared/dividers";
import { FlexColumn, FlexRow } from "@@/shared/flex-containers";
import { Icon } from "@@/shared/icons/icon";
import { Label } from "@@/shared/label";
import { LayoutCell } from "@@/shared/layout-cell";
import { LayoutGrid } from "@@/shared/layout-grid";
import { ImageV2 } from "@@/shared/pictures/image";
import { usePageBreakpoint } from "@@/shared/responsiveness/use-page-breakpoint";
import { TextBox } from "@@/shared/text";
import { usePriceFormatter } from "@@/translations/use-price-formatter";
import { useTranslate } from "@@/translations/use-translate";
import { useTheme } from "@emotion/react";
import {
    faClipboardCheck,
    faGift,
    faHeart,
} from "@fortawesome/pro-solid-svg-icons";
import type { GiftCard_Product, Provider, RemSize } from "@towni/common";
import {
    isAlfredItemActive,
    isNotUndefined,
    joinTranslatables,
    priceForHumansFactory,
    providerStatusTypeMap,
    translation,
} from "@towni/common";
import * as React from "react";

import { _BackButton } from "@@/routes/products/$productId/_bookable/generic/_back-button";
import { _ContentBlocks } from "@@/routes/products/$productId/_bookable/generic/_content-blocks";
import { GiftCardPricePicker } from "@@/routes/products/$productId/_gift_card/gift-card-price-picker";
import { AddGiftCardToCartWithQuantityButton } from "@@/routes/products/$productId/_gift_card/gift-card.add-gift-card-to-cart-button";
import { FadedBottomContainer } from "@@/shared/buttons/faded-bottom-container";
import { isRunningAsPwa } from "@@/shared/is-running-as-pwa";

type Props = {
    readonly product: GiftCard_Product;
    readonly provider: Provider;
    readonly hideLinkToShop: boolean;
};

const GiftCardPurchaseV2 = (props: Props) => {
    const theme = useTheme();
    const isMedium = usePageBreakpoint({ when: "M" });
    const isMediumSmall = usePageBreakpoint({ when: "MS" });
    const isMobile = usePageBreakpoint({ when: "📱" });
    const { product, provider } = props;
    const providerStatus = useCalculatedProviderStatus(provider);
    const productDisabled =
        providerStatus === "PAUSED" ||
        (product.status && !isAlfredItemActive(product.status));

    const headerContent = (
        <>
            <_BackButton provider={provider} />
            <Conditional when={!isMobile}>
                <HorizontalDivider />
                <CurrentUserIcon />
            </Conditional>
        </>
    );

    const oneColumn = isMobile || isMediumSmall;

    const [{ show }, modalId] = useModal("cart_modal__");

    const purchaseContext = useGiftCardPurchaseContext();
    const priceFormat = usePriceFormatter();

    const disabled =
        providerStatus === providerStatusTypeMap.PAUSED ||
        (product?.status && !isAlfredItemActive(product?.status));

    React.useEffect(
        function checkIfAddToCartButtonShouldBeDisabled() {
            const addToCartDisabled =
                disabled || !purchaseContext.selectedPrice;

            if (addToCartDisabled !== purchaseContext.addToCartDisabled) {
                // Set button as disabled
                purchaseContext.setAddToCartDisabled(addToCartDisabled);
            }
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [
            purchaseContext.selectedPrice,
            purchaseContext.addToCartDisabled,
            purchaseContext.setAddToCartDisabled,
            disabled,
        ],
    );

    const [, cartItemGenerationErrors] = purchaseContext.getOrderItems();

    cartItemGenerationErrors.forEach(error => {
        browserLogger.error(error);
    });

    const hasPriceOptions =
        !!product?.priceSettings &&
        (product.priceSettings?.options.length > 1 ||
            !!product.priceSettings.freePricingEnabled);
    React.useEffect(() => {
        if (!product) {
            // there can be no price
            return;
        }
        if (!hasPriceOptions || !product.priceSettings) {
            // Gift card only has one price option
            purchaseContext.setSelectedPrice(product.price);
            return;
        }

        // Gift card has multiple price options
        // if there is a default value set it as preselected price in context
        if (product.priceSettings.defaultAmountInclVat) {
            purchaseContext.setSelectedPrice(
                priceForHumansFactory({
                    amountIncludingVat:
                        product.priceSettings.defaultAmountInclVat,
                    vat: product.priceSettings.vat,
                }),
            );
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [hasPriceOptions, product, purchaseContext.setSelectedPrice]);

    return (
        <>
            <LayoutGrid
                className="shadow"
                css={{
                    label: "layout-grid",
                    marginTop: oneColumn ? 0 : 20,
                    marginBottom: oneColumn ? 0 : 100,

                    flex: 1,
                    backgroundColor: theme.colors.white.asString,
                    borderRadius: oneColumn ? 0 : 20,
                    gridTemplateColumns:
                        isMobile || isMediumSmall
                            ? "1fr"
                            : "minmax(0, 1fr) 10px minmax(0, 1fr)",
                    gridTemplateRows: "1fr",
                    gridAutoRows: "min-content",
                    paddingBottom: oneColumn ? 0 : 120, //
                    opacity: productDisabled ? 0.8 : 1,
                    filter: productDisabled ? "grayscale(100%)" : "unset",
                    pointerEvents: productDisabled ? "none" : "unset",

                    position: "relative",
                    //  overflow: "clip",
                }}>
                <Conditional when={isMobile}>
                    {/* HEADER */}
                    <LayoutCell
                        css={{
                            padding: 20,
                            gridRow: 1,
                            label: "back_button_area",
                            display: "flex",
                            justifyContent: "space-between",
                            zIndex: 9999,
                            borderBottom: "none",
                            gridColumn: "1 / -1",
                            alignItems: "flex-start",
                            position: "sticky",
                            top: 0,
                            alignSelf: "start",
                            backgroundColor: theme.colors.transparent.asString,
                        }}>
                        {headerContent}
                    </LayoutCell>
                </Conditional>

                {/* IMAGES_AREA */}
                <LayoutCell
                    css={{
                        label: "images_area",
                        display: "flex",
                        justifyContent: "stretch",
                        alignTracks: "stretch",
                        gridColumn: "1 / -1",
                        gridRow: 1,
                        paddingTop: 0,
                        paddingLeft: 0,
                        paddingRight: 0,
                        position: "relative",
                        height: "fit-content",
                        overflow: "hidden",
                        backgroundColor:
                            theme.colors.default.background.asString,
                        borderRadius: isMobile ? 0 : 10,
                        ...(!isMobile
                            ? {
                                  marginTop: (isMedium ? 27 : 54) / 2,
                                  marginLeft: (isMedium ? 30 : 60) / 2,
                                  marginRight: (isMedium ? 30 : 60) / 2,
                                  marginBottom: (isMedium ? 30 : 40) / 2,
                              }
                            : {}),
                    }}>
                    <_ProductImages
                        product={product}
                        provider={provider}
                        fallbackOnProvider
                    />
                    <div
                        css={{
                            position: "absolute",
                            bottom: 20,
                            left: 20,
                            zIndex: 100,
                            label: "LOGO",
                        }}>
                        <ImageV2
                            imageSource={provider.logoImageRef}
                            title={joinTranslatables([
                                provider.meta.title,
                                " - Logo",
                            ])}
                            css={{
                                width: 60,
                                height: 60,
                                borderRadius: 3000,
                                overflow: "hidden",
                            }}
                        />
                    </div>
                </LayoutCell>

                {/* COLUMN_ONE */}
                <LayoutCell
                    css={{
                        gridColumn: 1,
                        label: "column_one",
                        paddingTop: 20,
                        paddingLeft: 20,
                        paddingRight: 20,
                        backgroundColor:
                            theme.colors.default.background.asString,
                        ...(!isMobile
                            ? {
                                  paddingLeft: isMedium ? 35 : 60,
                                  paddingRight: oneColumn
                                      ? isMedium
                                          ? 35
                                          : 60
                                      : 0,

                                  paddingTop: 0,
                              }
                            : {}),
                    }}>
                    <Conditional when={productDisabled}>
                        <Label
                            text={translation({
                                sv: "Ej tillgänglig",
                                en: "Unavailable",
                            })}
                            colorTheme="YELLOW"
                            size={1.5}
                        />
                        <VerticalDivider L />
                    </Conditional>
                    <ColumnOne product={product} provider={provider} />
                    <Conditional when={oneColumn}>
                        <VerticalDivider L />
                        <HorizontalLine fillParentWidth />
                        <VerticalDivider S />
                    </Conditional>
                </LayoutCell>

                <Conditional when={!oneColumn}>
                    {/* SPACER */}
                    <LayoutCell
                        css={{
                            label: "spacer",
                            gridColumn: 2,
                        }}
                    />
                </Conditional>

                {/* COLUMN_TWO */}
                <LayoutCell
                    css={{
                        label: "book_area",
                        gridColumn: 1,
                        paddingLeft: 20,
                        paddingRight: 20,
                        backgroundColor:
                            theme.colors.default.background.asString,
                        ...(!isMobile
                            ? {
                                  gridColumn: oneColumn ? 1 : 3,
                                  paddingLeft: oneColumn
                                      ? isMedium
                                          ? 35
                                          : 60
                                      : 0,
                                  paddingRight: isMedium ? 35 : 60,
                              }
                            : {}),
                    }}>
                    <ColumnTwo product={product} provider={provider} />

                    <Conditional
                        when={isAlfredItemActive(props.product?.status)}
                        render={() => (
                            <FadedBottomContainer position="sticky">
                                <AddGiftCardToCartWithQuantityButton
                                    afterAddNavigation={() => {
                                        void show();
                                    }}
                                    addButtonText={
                                        <FlexRow
                                            mainAxis="space-between"
                                            crossAxis="center">
                                            <TextBox
                                                text={joinTranslatables([
                                                    translation({
                                                        sv: "Köp",
                                                        en: "Buy",
                                                    }),
                                                    ...(!purchaseContext.selectedPrice
                                                        ? []
                                                        : [
                                                              `  ${priceFormat(purchaseContext.selectedPrice)}`,
                                                          ]),
                                                ])}
                                                weight="700"
                                            />
                                        </FlexRow>
                                    }
                                    clearBeforeAdding={true}
                                    padding={
                                        isRunningAsPwa
                                            ? {
                                                  top: 15,
                                                  bottom: 25,
                                                  leftRight: 20,
                                              }
                                            : { bottom: 20 }
                                    }
                                />
                            </FadedBottomContainer>
                        )}
                    />
                </LayoutCell>
            </LayoutGrid>

            <CartProviderCheckModal providerId={product?.providerId} />
            <ProviderPauseCheckModal providerId={product?.providerId} />
            <CartModal modalId={modalId} />
        </>
    );
};

const ColumnOne = (props: {
    product: GiftCard_Product;
    provider: Provider;
}) => {
    const theme = useTheme();
    const { product } = props;
    return (
        <FlexColumn fillParentWidth tag="_column_one">
            {/* Kicker */}
            <Conditional
                when={!!product.meta.kicker}
                render={() => (
                    <TextBox
                        text={product.meta.kicker}
                        color={theme.colors.primary}
                        weight="900"
                        dataTestId="kicker"
                        size={0.8}
                        css={{
                            label: "kicker",
                        }}
                    />
                )}
            />
            <VerticalDivider XS />
            {/* Title */}
            <TextBox
                text={product.meta.title}
                size={2.2}
                weight="900"
                dataTestId="product_title"
                userSelect="text"
                css={{
                    label: "title",
                }}
            />
            <VerticalDivider S />
            {/* Subtitle */}
            <Conditional
                when={!!product.meta.subtitle}
                render={() => (
                    <>
                        <TextBox
                            text={product.meta.subtitle}
                            size={1.2}
                            dataTestId="product_subtitle"
                            color={theme.colors.black.light70}
                            weight="500"
                            userSelect="text"
                            css={{
                                label: "subtitle",
                            }}
                        />
                        <VerticalDivider M />
                        <HorizontalLine />
                        <VerticalDivider M />
                    </>
                )}
            />

            {/* Description */}
            <Conditional
                when={!!product.meta.description}
                render={() => (
                    <>
                        <TextBox
                            text={product.meta.description}
                            userSelect="text"
                            dataTestId="product_description"
                            lineClamp={{
                                lines: 5,
                                expandable: true,
                            }}
                            size={1}
                            renderMarkdown={true}
                            css={{
                                label: "description",
                            }}
                        />
                        <VerticalDivider M />
                        <HorizontalLine />
                        <VerticalDivider M />
                    </>
                )}
            />

            {/* Info block */}
            <_ContentBlocks product={product} />
        </FlexColumn>
    );
};

const ColumnTwo = (props: {
    product: GiftCard_Product;
    provider: Provider;
}) => {
    const theme = useTheme();

    const { product, provider } = props;

    const providerStatus = useCalculatedProviderStatus(provider);

    const translate = useTranslate();
    const purchaseContext = useGiftCardPurchaseContext();

    const disabled =
        providerStatus === providerStatusTypeMap.PAUSED ||
        (product?.status && !isAlfredItemActive(product?.status));

    React.useEffect(
        function checkIfAddToCartButtonShouldBeDisabled() {
            const addToCartDisabled =
                disabled || !purchaseContext.selectedPrice;

            if (addToCartDisabled !== purchaseContext.addToCartDisabled) {
                // Set button as disabled
                purchaseContext.setAddToCartDisabled(addToCartDisabled);
            }
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [
            purchaseContext.selectedPrice,
            purchaseContext.addToCartDisabled,
            purchaseContext.setAddToCartDisabled,
            disabled,
        ],
    );

    const [, cartItemGenerationErrors] = purchaseContext.getOrderItems();

    cartItemGenerationErrors.forEach(error => {
        browserLogger.error(error);
    });

    const hasPriceOptions =
        !!product?.priceSettings &&
        (product.priceSettings?.options.length > 1 ||
            !!product.priceSettings.freePricingEnabled);
    React.useEffect(() => {
        if (!product) {
            // there can be no price
            return;
        }
        if (!hasPriceOptions || !product.priceSettings) {
            // Gift card only has one price option
            purchaseContext.setSelectedPrice(product.price);
            return;
        }

        // Gift card has multiple price options
        // if there is a default value set it as preselected price in context
        if (product.priceSettings.defaultAmountInclVat) {
            purchaseContext.setSelectedPrice(
                priceForHumansFactory({
                    amountIncludingVat:
                        product.priceSettings.defaultAmountInclVat,
                    vat: product.priceSettings.vat,
                }),
            );
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [hasPriceOptions, product, purchaseContext.setSelectedPrice]);

    return (
        <>
            <FlexColumn
                grow={1}
                shrink={0}
                fillParent
                mainAxis="space-between"
                background={{
                    color: theme.colors.white.asString,
                }}
                crossAxis="stretch">
                <FlexColumn css={{ flexGrow: 1, flexShrink: 0 }}>
                    {/* <Conditional
                        when={!!provider}
                        render={() => {
                            if (!provider)
                                throw new Error("Provider is undefined");
                            return (
                                <>
                                    <VerticalDivider S />
                                    <FlexRow mainAxis="center">
                                        <LinkTo to={providerHomePath}>
                                            <TextBox
                                                text={translation({
                                                    sv: `Se fler produkter från ${translate(provider.meta.title)} `,
                                                    en: `See more products from ${translate(provider.meta.title)}`,
                                                })}
                                                size="XXS"
                                                weight="400"
                                                case="UPPERCASE"
                                                styling={linkCss.white70}
                                            />
                                        </LinkTo>
                                    </FlexRow>
                                    <VerticalDivider M />
                                </>
                            );
                        }}
                    />
                    <HorizontalSectionDivider /> */}
                    {!disabled &&
                    isNotUndefined(product) &&
                    product.priceSettings &&
                    (product.priceSettings?.options.length > 1 ||
                        product.priceSettings.freePricingEnabled) ? (
                        <>
                            <GiftCardPricePicker
                                giftCard={product}
                                onSelect={purchaseContext.setSelectedPrice}
                            />
                            <HorizontalLine />
                            {/* <HorizontalSectionDivider /> */}
                        </>
                    ) : null}
                    <FlexColumn fillParentWidth padding={{ leftRight: 0 }}>
                        <VerticalDivider M />
                        <GiftCardBlock />
                        <VerticalDivider M />
                    </FlexColumn>
                </FlexColumn>
            </FlexColumn>
        </>
    );
};

const GiftCardBlock = () => {
    const theme = useTheme();
    return (
        <FlexColumn>
            <FlexRow crossAxis="flex-start">
                <FlexColumn width="28px" crossAxis="flex-start">
                    <Icon
                        icon={faClipboardCheck}
                        color={theme.colors.primary}
                        size="M"
                        padding={{ top: 2 }}
                    />
                </FlexColumn>
                <HorizontalDivider />
                <FlexColumn width="100%">
                    <FlexRow>
                        <TextBox
                            text={translation({
                                sv: "Levereras direkt",
                                en: "Delivered instantly",
                            })}
                            userSelect="text"
                            size={1}
                            weight={"700"}
                        />
                    </FlexRow>
                    <VerticalDivider XXS />
                    <TextBox
                        text={translation({
                            sv: "Ni får digital leverans direkt via e-post och sms. Done and done!",
                            en: "You get digital delivery directly via email and sms. Done and done!",
                        })}
                        userSelect="text"
                        size="S"
                        weight="400"
                        color={theme.colors.black.light30}
                        css={{
                            lineHeight: 1.3,
                        }}
                    />
                    <VerticalDivider M />
                </FlexColumn>
            </FlexRow>
            <FlexRow crossAxis="flex-start">
                <FlexColumn width="28px" crossAxis="flex-start">
                    <Icon
                        icon={faGift}
                        color={theme.colors.primary}
                        size="M"
                        padding={{ top: 2 }}
                    />
                </FlexColumn>
                <HorizontalDivider />
                <FlexColumn width="100%">
                    <FlexRow>
                        <TextBox
                            text={translation({
                                sv: "Alltid med - tappas inte bort",
                                en: "Always with you - never lost",
                            })}
                            userSelect="text"
                            size={1}
                            weight={"700"}
                        />
                    </FlexRow>
                    <VerticalDivider XXS />
                    <TextBox
                        text={translation({
                            sv: "Ingen papperslapp som tappas bort. I mobilen och molnet så är det alltid med.",
                            en: "No paper note that gets lost. In the phone and the cloud, it's always with you.",
                        })}
                        userSelect="text"
                        size="S"
                        weight="400"
                        color={theme.colors.black.light30}
                        css={{
                            lineHeight: 1.3,
                        }}
                    />
                    <VerticalDivider M />
                </FlexColumn>
            </FlexRow>
            <FlexRow crossAxis="flex-start">
                <FlexColumn width="28px" crossAxis="flex-start">
                    <Icon
                        icon={faHeart}
                        color={theme.colors.primary}
                        size={1 as RemSize}
                        padding={{ top: 2 }}
                    />
                </FlexColumn>
                <HorizontalDivider />
                <FlexColumn width="100%">
                    <FlexRow>
                        <TextBox
                            text={translation({
                                sv: "Enkelt att dela eller ge bort",
                                en: "Easy to share or give away",
                            })}
                            userSelect="text"
                            size={1}
                            weight={"700"}
                        />
                    </FlexRow>
                    <VerticalDivider XXS />
                    <TextBox
                        text={translation({
                            sv: "Ni kan direkt dela presentkortet och den som tar emot får en fin länk direkt till gåvan. Går också att skriva ut (och slå in med knorr!)",
                            en: "You can directly share the gift card and the recipient gets a nice link directly to the gift. Can also be printed (and wrapped with a bow!)",
                        })}
                        userSelect="text"
                        size="S"
                        weight="400"
                        color={theme.colors.black.light30}
                        css={{
                            lineHeight: 1.3,
                        }}
                    />
                </FlexColumn>
            </FlexRow>
        </FlexColumn>
    );
};

export { GiftCardPurchaseV2 };
